import React from 'react';
import {
  Box, HStack, Text, Button, Icon,
} from '@gluestack-ui/themed';
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react-native';

interface EvaluationTablePaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

/**
 * Simple pagination controls for the evaluation table.
 * Reuses the admin pattern for consistency.
 */
export const EvaluationTablePagination: React.FC<EvaluationTablePaginationProps> = ({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  isLoading = false,
}) => {
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = totalItems ? 1 + (currentPage - 1) * pageSize : 0;
  const end = totalItems ? Math.min(totalItems, currentPage * pageSize) : 0;

  const leftDisabled = currentPage === 1 || isLoading || !totalItems;
  const rightDisabled = currentPage === totalPages || isLoading || !totalItems;

  return (
    <Box justifyContent="center" mt="$2" px="$6" py="$3">
      <HStack alignItems="center" justifyContent="space-between">
        <Text size="sm" color="$textSubtle">
          {totalItems ? `Showing ${start}-${end} of ${totalItems}` : 'No results'}
        </Text>
        <HStack space="xs" alignItems="center">
          <Button
            variant="link"
            size="sm"
            isDisabled={leftDisabled}
            onPress={() => onPageChange(1)}
          >
            <Icon as={ChevronsLeft} size="sm" color={leftDisabled ? '$textHint' : '$textBody'} />
          </Button>

          <Button
            variant="link"
            size="sm"
            isDisabled={leftDisabled}
            onPress={() => onPageChange(Math.max(1, currentPage - 1))}
          >
            <Icon as={ChevronLeft} size="sm" color={leftDisabled ? '$textHint' : '$textBody'} />
          </Button>

          <Text size="sm" mx="$2" color="$textBody">
            Page
            {' '}
            {currentPage}
            {' '}
            of
            {' '}
            {totalPages}
          </Text>

          <Button
            variant="link"
            size="sm"
            isDisabled={rightDisabled}
            onPress={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          >
            <Icon as={ChevronRight} size="sm" color={rightDisabled ? '$textHint' : '$textBody'} />
          </Button>

          <Button
            variant="link"
            size="sm"
            isDisabled={rightDisabled}
            onPress={() => onPageChange(totalPages)}
          >
            <Icon as={ChevronsRight} size="sm" color={rightDisabled ? '$textHint' : '$textBody'} />
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

export default EvaluationTablePagination;
